import { useEffect, useState } from "react";
import { getData, postData } from "../backend";
import { HubspotPickerData, WorkflowAutomation } from "../interfaces";
import AppHubspotPickerAssociatedObjects from "./AppHubspotPickerAssociatedObjects";
import AppHubspotPickerSignatureEmails from "./AppHubspotPickerSignatureEmails";

function AppHubspotPicker() {

  const queryParams = new URLSearchParams(window.location.search);
  const payload = queryParams.get('payload');

  const [pickerData, setPickerData] = useState<HubspotPickerData | null>();
  const [selectedObjects, setSelectedObjects] = useState<Record<string, boolean>>();
  const [signatureEmailOverrides, setSignatureEmailOverrides] = useState<Array<string>>();

  const [currentAutomation, setCurrentAutomation] = useState<WorkflowAutomation>();

  const [error, setError] = useState<string>();
  const [isStarting, setIsStarting] = useState(false);

  const status = !!error ? "ERROR" : currentAutomation?.status ?? (isStarting ? "PENDING" : "INITIAL");
  const active = isStarting || status === "PENDING" || status === "RUNNING";

  const associatedObjects = pickerData?.associatedObjectOptions;

  const [currentPage, setCurrentPage] = useState<"ASSOCIATED_OBJECTS" | "SIGNATURE_EMAILS">("ASSOCIATED_OBJECTS");

  useEffect(() => {
    if (pickerData !== undefined)
      return;

    getData(`/hubspot/associated-objects/${payload}/`)
      .then((data: HubspotPickerData) => {
        setPickerData(data);

        setSelectedObjects(data.associatedObjectOptions.reduce((acc: Record<string, boolean>, objList: any) => {
          objList.items.forEach((obj: any) => acc[`${objList.toId}.${obj.id}`] = true);
          return acc;
        }, {} as Record<string, boolean>));
      })
      .catch(() => setPickerData(null));
  }, [payload, pickerData]);

  function toggleSelectedObject(id: string) {
    if (active)
      return;

    setSelectedObjects((prev) => {
      if (prev === undefined)
        return { [id]: true };

      const newValue = !prev[id];
      return { ...prev, [id]: newValue };
    });
  }

  function setAllSelectedObjects(objectType: string, checked: boolean) {
    if (active)
      return;

    setSelectedObjects((prev) => {
      if (prev === undefined)
        prev = {};

      for (const id of Object.keys(prev)) {
        if (id.startsWith(objectType + "."))
          prev[id] = checked;
      }

      return { ...prev };
    });
  }

  function handleStartAutomation() {
    if (pickerData?.signatureRequestNumRecipients && currentPage === "ASSOCIATED_OBJECTS")
      return setCurrentPage("SIGNATURE_EMAILS");

    setIsStarting(true);

    const associatedObjectIds = (() => {
      if (selectedObjects === undefined)
        return undefined;

      return Object.entries(selectedObjects)
        .filter(([_, value]) => value)
        .map(([key, _]) => key);
    })()

    postData(`/hubspot/associated-objects/${payload}/`, { associatedObjectIds, signatureEmailOverrides })
      .then((automation) => setCurrentAutomation(automation))
      .catch(setError)
      .finally(() => setIsStarting(false));
  }

  return (
    <div className="flex flex-col w-screen px-2 h-screen">
      {currentPage === "ASSOCIATED_OBJECTS" &&
        <AppHubspotPickerAssociatedObjects options={associatedObjects} selectedObjects={selectedObjects} toggleSelectedObject={toggleSelectedObject} setAllSelectedObjects={setAllSelectedObjects} active={active} />
      }

      {currentPage === "SIGNATURE_EMAILS" &&
        <AppHubspotPickerSignatureEmails pickerData={pickerData!} signatureRequestRecipients={signatureEmailOverrides} setSignatureRequestRecipients={setSignatureEmailOverrides} active={active} onClickBack={() => setCurrentPage("ASSOCIATED_OBJECTS")} />
      }

      {(status === "INITIAL" || status === "COMPLETED" || status === "CANCELLED") &&
        <button className="btn btn-blue w-full mt-auto" onClick={handleStartAutomation}>
          {(currentPage === "ASSOCIATED_OBJECTS" && pickerData?.signatureRequestNumRecipients) ? "Next" : "Start Automation"}
        </button>
      }

      {isStarting &&
        <button className="btn btn-blue w-full mt-auto" disabled>
          Starting...
        </button>
      }

      {!isStarting && (status === "PENDING" || status === "RUNNING") &&
        <div className="text-blue font-semibold text-center text-sm mt-auto">
          This automation has been started and will be complete shortly. This window can now be closed.
        </div>
      }

      {(status === "ERROR") &&
        <div className="text-red font-semibold text-center  text-sm mt-auto">
          An error occured when starting this automation. Please contact support.
        </div>
      }
    </div>
  )
}

export default AppHubspotPicker;
